import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectTemplatesService } from '../api/project-templates.service';
import { PROJECT_TEMPLATE_KEYS } from './queryKeys';
import { countTemplateStatuses, countTemplateTasks } from '../utils/templateStats';
import type { ProjectTemplateFilters, TemplateGalleryItem } from '../types';

/**
 * Fetches the template list and enriches each row with its detail-derived `description`/
 * `taskCount`/`statusCount` — the summary endpoint doesn't carry them (see `types.ts`'s
 * `TemplateGalleryItem` doc and `tasks/project-templates-backend-contract.md` §7).
 */
export function useProjectTemplates(filters: ProjectTemplateFilters, enabled = true) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: PROJECT_TEMPLATE_KEYS.list(orgId ?? '', filters),
    queryFn: async ({ signal }): Promise<TemplateGalleryItem[]> => {
      const { data: summaries } = await projectTemplatesService.getList(filters, signal);
      const details = await Promise.all(
        summaries.map((summary) => projectTemplatesService.getById(summary.id, signal)),
      );
      return summaries.map((summary, index) => {
        const template = details[index].data;
        return {
          ...summary,
          description: template.description,
          taskCount: countTemplateTasks(template),
          statusCount: countTemplateStatuses(template),
        };
      });
    },
    staleTime: 300_000,
    enabled: !!orgId && enabled,
  });

  return {
    templates: data ?? [],
    isLoading,
    isError,
    refetch,
  };
}
